import React, { Component } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { useHistory, useLocation } from 'react-router-dom';
import omit from 'lodash/omit';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';

import { useIntl, FormattedMessage } from '../../util/reactIntl';
import {
  isAnyFilterActive,
  isMainSearchTypeKeywords,
  isOriginInUse,
  getQueryParamNames,
} from '../../util/search';
import {
  NO_ACCESS_PAGE_USER_PENDING_APPROVAL,
  NO_ACCESS_PAGE_VIEW_LISTINGS,
  parse,
} from '../../util/urlHelpers';
import { createResourceLocatorString } from '../../util/routes';
import { propTypes } from '../../util/types';
import {
  isErrorNoViewingPermission,
  isErrorUserPendingApproval,
  isForbiddenError,
} from '../../util/errors';
import {
  hasPermissionToViewData,
  isUserAuthorized,
  showCreateListingLinkForUser,
} from '../../util/userHelpers';
import { getListingsById } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/ui.duck';

import { Button, H3, H5, Icons, LayoutSingleColumn, NamedRedirect, Page } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import {
  groupListingFieldConfigs,
  initialValues,
  searchParamsPicker,
  validUrlQueryParamsFromProps,
  validFilterParams,
  cleanSearchFromConflictingParams,
  createSearchResultSchema,
  pickListingFieldFilters,
  omitLimitedListingFieldParams,
  getDatesAndSeatsMaybe,
  getSearchPageResourceLocatorStringParams,
} from './SearchPage.shared';

import FilterComponent from './FilterComponent';
import MainPanelHeader from './MainPanelHeader/MainPanelHeader';
import SearchFiltersMobile from './SearchFiltersMobile/SearchFiltersMobile';
import SortBy from './SortBy/SortBy';
import SearchResultsPanel from './SearchResultsPanel/SearchResultsPanel';
import NoSearchResultsMaybe from './NoSearchResultsMaybe/NoSearchResultsMaybe';

import css from './SearchPage.module.css';
import SearchCategorySection from './SearchCategorySection';
import TrendingSection from './TrendingSection/TrendingSection';
import { customFilters } from '../../config/configSearch';
import { getFilterPriority } from '../../util/genericHelpers';
import { withViewport } from '../../util/uiHelpers';

import Drawer from 'react-modern-drawer';
import '../TopbarContainer/Topbar/Drawer.css';
const MODAL_BREAKPOINT = 768; // Search is in modal on mobile layout

// SortBy component has its content in dropdown-popup.
// With this offset we move the dropdown a few pixels on desktop layout.
const FILTER_DROPDOWN_OFFSET = -14;

export class SearchPageComponent extends Component {
  constructor(props) {
    super(props);

    this.state = {
      isMobileModalOpen: false,
      currentQueryParams: validUrlQueryParamsFromProps(props),

      isOpen: false,
      panels: [],
    };

    this.toggleDrawer = this.toggleDrawer.bind(this);
    this.onOpenMobileModal = this.onOpenMobileModal.bind(this);
    this.onCloseMobileModal = this.onCloseMobileModal.bind(this);

    // Filter functions
    this.resetAll = this.resetAll.bind(this);
    this.getHandleChangedValueFn = this.getHandleChangedValueFn.bind(this);

    // SortBy
    this.handleSortBy = this.handleSortBy.bind(this);
  }

  getAvailableFilters() {
    const { location, routeConfiguration, config, params: currentPathParams = {} } = this.props;

    const { listingFields } = config?.listing || {};
    const { defaultFilters: defaultFiltersRaw, sortConfig } = config?.search || {};

    const activeListingTypes = config?.listing?.listingTypes.map(config => config.listingType);
    const defaultFiltersConfig = currentPathParams.listingType
      ? defaultFiltersRaw.filter(f => f.key !== 'listingType')
      : defaultFiltersRaw;

    const categoryConfiguration = config.categoryConfiguration;
    const listingCategories = categoryConfiguration.categories;
    const listingFieldsConfig = pickListingFieldFilters({
      listingFields,
      locationSearch: location.search,
      categoryConfiguration,
      activeListingTypes,
      currentPathParams,
    });
    const filterConfigs = {
      listingFieldsConfig,
      defaultFiltersConfig,
      listingCategories,
      activeListingTypes,
      currentPathParams,
    };

    const isKeywordSearch = isMainSearchTypeKeywords(config);
    const builtInPrimaryFilters = defaultFiltersConfig.filter(f =>
      ['categoryLevel', 'listingType'].includes(f.key)
    );
    const builtInFilters = isKeywordSearch
      ? defaultFiltersConfig.filter(
          f => !['keywords', 'categoryLevel', 'listingType'].includes(f.key)
        )
      : defaultFiltersConfig.filter(f => !['categoryLevel', 'listingType'].includes(f.key));
    const [customPrimaryFilters, customSecondaryFilters] = groupListingFieldConfigs(
      listingFieldsConfig,
      activeListingTypes
    );
    let availableFilters = [
      ...customFilters,
      ...builtInPrimaryFilters,
      ...customPrimaryFilters,
      ...builtInFilters,
      ...customSecondaryFilters,
    ].filter(f => !['listingType', 'categoryLevel'].includes(f.key));

    availableFilters = availableFilters.sort((a, b) => {
      const priorityA = getFilterPriority(a.key);
      const priorityB = getFilterPriority(b.key);

      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      }

      return 0;
    });

    return availableFilters;
  }

  // Invoked when a modal is opened from a child component,
  // for example when a filter modal is opened in mobile view
  onOpenMobileModal() {
    this.setState({ isMobileModalOpen: true });
  }

  toggleDrawer = () => this.setState(p => ({ isOpen: !p.isOpen }));

  // Open root menu
  openMenu = () => {
    const availableFilters = this.getAvailableFilters();
    const rootItems = availableFilters.map(f => ({
      id: `filter-${f.key}`,
      label: f.filterConfig?.label || f.label || f.key,
      filter: f,
    }));
    this.setState({
      isOpen: true,
      panels: [{ key: 'root', title: 'Filters', items: rootItems }],
    });
  };

  closeMenu = () => this.setState({ isOpen: false, panels: [] });

  // Push a new panel (submenu)
  pushPanel = ({ title, items, filterConfig }) => {
    const panel = { key: `${title}-${Date.now()}`, title };
    if (items) panel.items = items;
    if (filterConfig) panel.filterConfig = filterConfig;
    this.setState(prev => ({
      panels: [...prev.panels, panel],
    }));
  };

  // Pop current panel
  popPanel = () => {
    this.setState(prev => {
      if (prev.panels.length <= 1) return { isOpen: false, panels: [] };
      return { panels: prev.panels.slice(0, -1) };
    });
  };

  handleNodeClick = node => {
    if (node.filter) {
      this.pushPanel({ title: node.label, filterConfig: node.filter });
      return;
    }
    const hasChildren = Array.isArray(node.children) && node.children.length > 0;
    if (hasChildren) {
      this.pushPanel({ title: node.label, items: node.children.map(n => ({ ...n })) });
    } else {
      this.handleLeafClick(node);
    }
  };

  handleLeafClick = item => {
    // Replace with navigation/action
    console.log('Leaf clicked:', item);
    // Example: close after selection
    // this.closeMenu();
  };

  renderCurrentPanel = () => {
    const { panels } = this.state;
    if (!panels.length) return null;
    const current = panels[panels.length - 1];

    const {
      intl,
      location,
      config,
      params: currentPathParams = {},
      pagination,
      searchInProgress,
      searchParamsAreInSync,
    } = this.props;
    const marketplaceCurrency = config.currency;
    const categoryConfiguration = config.categoryConfiguration;
    const listingCategories = categoryConfiguration.categories;
    const validQueryParams = validUrlQueryParamsFromProps(this.props);
    const hasPaginationInfo = !!pagination && pagination.totalItems != null;
    const totalItems =
      searchParamsAreInSync && hasPaginationInfo
        ? pagination.totalItems
        : pagination?.paginationUnsupported
        ? listings.length
        : 0;

    if (current.filterConfig) {
      const filterConfig = current.filterConfig;
      const key = `MobileFilter.${filterConfig.scope || 'built-in'}.${filterConfig.key}`;

      return (
        <section
          key={current.key}
          className={classNames(css.panel, css.panelAnimation)}
          aria-label={current.title}
        >
          {this.state.panels.length > 1 && (
            <div className={css.panelHeader} onClick={this.popPanel}>
              <button
                type="button"
                className={css.back}
                aria-label="Back"
                disabled={panels.length <= 1}
              >
                <Icons name="leftAngle" />
              </button>
              <h3 className={css.panelTitle}>{current.title}</h3>
            </div>
          )}

          <div className={css.filterPanelContent}>
            <FilterComponent
              key={key}
              idPrefix="SearchFiltersMobile"
              config={filterConfig}
              listingCategories={listingCategories}
              marketplaceCurrency={marketplaceCurrency}
              urlQueryParams={validQueryParams}
              initialValues={initialValues(this.props, this.state.currentQueryParams)}
              getHandleChangedValueFn={this.getHandleChangedValueFn}
              intl={intl}
              liveEdit
              showAsPopup={false}
              isSearchDrawerFilter={true}
            />
          </div>
        </section>
      );
    }

    return (
      <section
        key={current.key}
        className={classNames(css.panel, css.panelAnimation)}
        aria-label={current.title}
      >
        {this.state.panels.length > 1 && (
          <div className={css.panelHeader} onClick={this.popPanel}>
            <button
              type="button"
              className={css.back}
              aria-label="Back"
              disabled={panels.length <= 1}
            >
              <Icons name="leftAngle" />
            </button>
            <h3 className={css.panelTitle}>{current.title}</h3>
          </div>
        )}

        <ul className={css.panelList} role="list">
          {current.items.map(node => {
            const hasChildren = Array.isArray(node.children) && node.children.length > 0;
            const isFilter = !!node.filter;
            return (
              <li key={node.id}>
                <span
                  type="button"
                  className={css.rowBtn}
                  onClick={() => this.handleNodeClick(node)}
                  aria-haspopup={hasChildren || isFilter ? 'true' : 'false'}
                >
                  <span
                    className={classNames(
                      css.rowLabel,
                      !(hasChildren || isFilter) ? css.link : null
                    )}
                  >
                    {node.label}
                  </span>
                  <span className={css.rowIcon} aria-hidden>
                    {hasChildren || isFilter ? <Icons name="rightAngle" /> : ''}
                  </span>
                </span>
              </li>
            );
          })}
        </ul>
      </section>
    );
  };

  // Invoked when a modal is closed from a child component,
  // for example when a filter modal is opened in mobile view
  onCloseMobileModal() {
    this.setState({ isMobileModalOpen: false });
  }

  // Reset all filter query parameters
  resetAll(e) {
    const { history, routeConfiguration, config, location } = this.props;
    const { listingFields: listingFieldsConfig } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig } = config?.search || {};

    const urlQueryParams = validUrlQueryParamsFromProps(this.props);
    const filterQueryParamNames = getQueryParamNames(listingFieldsConfig, defaultFiltersConfig);

    // Reset state
    this.setState({ currentQueryParams: {} });

    // Reset routing params
    const queryParams = omit(urlQueryParams, filterQueryParamNames);

    const { routeName, pathParams } = getSearchPageResourceLocatorStringParams(
      routeConfiguration,
      location
    );

    history.push(createResourceLocatorString(routeName, routeConfiguration, pathParams, {}));
  }

  getHandleChangedValueFn(useHistoryPush) {
    const {
      history,
      routeConfiguration,
      config,
      location,
      params: currentPathParams = {},
    } = this.props;
    const { listingFields: listingFieldsConfig } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig, sortConfig } = config?.search || {};
    const activeListingTypes = config?.listing?.listingTypes.map(config => config.listingType);
    const listingCategories = config.categoryConfiguration.categories;
    const filterConfigs = {
      listingFieldsConfig,
      defaultFiltersConfig,
      listingCategories,
      activeListingTypes,
      currentPathParams,
    };

    const urlQueryParams = validUrlQueryParamsFromProps(this.props);

    return updatedURLParams => {
      const updater = prevState => {
        const { address, bounds, keywords } = urlQueryParams;
        const mergedQueryParams = { ...urlQueryParams, ...prevState.currentQueryParams };

        // Address and bounds are handled outside of MainPanel.
        // I.e. TopbarSearchForm && search by moving the map.
        // We should always trust urlQueryParams with those.
        // The same applies to keywords, if the main search type is keyword search.
        const keywordsMaybe = isMainSearchTypeKeywords(config) ? { keywords } : {};
        const datesAndSeatsMaybe = getDatesAndSeatsMaybe(mergedQueryParams, updatedURLParams);
        return {
          currentQueryParams: omitLimitedListingFieldParams(
            {
              ...mergedQueryParams,
              ...updatedURLParams,
              ...keywordsMaybe,
              ...datesAndSeatsMaybe,
              address,
              bounds,
            },
            filterConfigs
          ),
        };
      };

      const callback = () => {
        if (useHistoryPush) {
          const searchParams = this.state.currentQueryParams;
          const search = cleanSearchFromConflictingParams(searchParams, filterConfigs, sortConfig);

          const { routeName, pathParams } = getSearchPageResourceLocatorStringParams(
            routeConfiguration,
            location
          );

          history.push(
            createResourceLocatorString(routeName, routeConfiguration, pathParams, search)
          );
        }
      };

      this.setState(updater, callback);
    };
  }

  handleSortBy(urlParam, values) {
    const { history, routeConfiguration, location } = this.props;
    const urlQueryParams = validUrlQueryParamsFromProps(this.props);

    const queryParams = values
      ? { ...urlQueryParams, [urlParam]: values }
      : omit(urlQueryParams, urlParam);

    const { routeName, pathParams } = getSearchPageResourceLocatorStringParams(
      routeConfiguration,
      location
    );

    history.push(
      createResourceLocatorString(routeName, routeConfiguration, pathParams, queryParams)
    );
  }

  // Reset all filter query parameters
  handleResetAll(e) {
    this.resetAll(e);

    // blur event target if event is passed
    if (e && e.currentTarget) {
      e.currentTarget.blur();
    }
  }

  render() {
    const {
      intl,
      listings = [],
      location,
      onManageDisableScrolling,
      pagination,
      scrollingDisabled,
      searchInProgress,
      searchListingsError,
      searchParams = {},
      routeConfiguration,
      config,
      viewport,
      params: currentPathParams = {},
      currentUser,
    } = this.props;
    const isMobileLayout = viewport?.width && viewport?.width > 0 && viewport?.width < 768;
    const isTrending = new URLSearchParams(location.search).get('pub_isTrending');
    // If the search page variant is of type /s/:listingType, this defines the :listingType
    // path parameter used to filter the whole page.
    //
    // On a default search page (/s), this constant does not have a value, even when a
    // query parameter ?pub_listingType=[queryParamListingType] is used.
    const { listingType: listingTypePathParam } = currentPathParams;

    const { listingFields } = config?.listing || {};
    const { defaultFilters: defaultFiltersRaw, sortConfig } = config?.search || {};

    const activeListingTypes = config?.listing?.listingTypes.map(config => config.listingType);
    const defaultFiltersConfig = listingTypePathParam
      ? defaultFiltersRaw.filter(f => f.key !== 'listingType')
      : defaultFiltersRaw;

    const marketplaceCurrency = config.currency;
    const categoryConfiguration = config.categoryConfiguration;
    const listingCategories = categoryConfiguration.categories;
    const listingFieldsConfig = pickListingFieldFilters({
      listingFields,
      locationSearch: location.search,
      categoryConfiguration,
      activeListingTypes,
      currentPathParams,
    });
    const filterConfigs = {
      listingFieldsConfig,
      defaultFiltersConfig,
      listingCategories,
      activeListingTypes,
      currentPathParams,
    };

    // Page transition might initially use values from previous search
    // urlQueryParams doesn't contain page specific url params
    // like mapSearch, page or origin (origin depends on config.maps.search.sortSearchByDistance)
    const { searchParamsAreInSync, urlQueryParams, searchParamsInURL } = searchParamsPicker(
      location.search,
      searchParams,
      filterConfigs,
      sortConfig,
      isOriginInUse(config)
    );
    const validQueryParams = urlQueryParams;

    const isKeywordSearch = isMainSearchTypeKeywords(config);
    const builtInPrimaryFilters = defaultFiltersConfig.filter(f =>
      ['categoryLevel', 'listingType'].includes(f.key)
    );
    const builtInFilters = isKeywordSearch
      ? defaultFiltersConfig.filter(
          f => !['keywords', 'categoryLevel', 'listingType'].includes(f.key)
        )
      : defaultFiltersConfig.filter(f => !['categoryLevel', 'listingType'].includes(f.key));

    const [customPrimaryFilters, customSecondaryFilters] = groupListingFieldConfigs(
      listingFieldsConfig,
      activeListingTypes
    );
    let availableFilters = [
      ...customFilters,
      ...builtInPrimaryFilters,
      ...customPrimaryFilters,
      ...builtInFilters,
      ...customSecondaryFilters,
    ].filter(f => !['listingType', 'categoryLevel'].includes(f.key));

    availableFilters = availableFilters.sort((a, b) => {
      const priorityA = getFilterPriority(a.key);
      const priorityB = getFilterPriority(b.key);

      // If priorities are different, sort by priority
      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      }

      // If same priority, maintain original order
      return 0;
    });
    // Selected aka active filters
    const selectedFilters = validQueryParams;
    const isValidDatesFilter =
      searchParamsInURL.dates == null ||
      (searchParamsInURL.dates != null && searchParamsInURL.dates === selectedFilters.dates);
    const keysOfSelectedFilters = Object.keys(selectedFilters);
    const selectedFiltersCountForMobile = isKeywordSearch
      ? keysOfSelectedFilters.filter(f => f !== 'keywords').length
      : keysOfSelectedFilters.length;

    const hasPaginationInfo = !!pagination && pagination.totalItems != null;
    const totalItems =
      searchParamsAreInSync && hasPaginationInfo
        ? pagination.totalItems
        : pagination?.paginationUnsupported
        ? listings.length
        : 0;
    const listingsAreLoaded =
      !searchInProgress &&
      searchParamsAreInSync &&
      !!(hasPaginationInfo || pagination?.paginationUnsupported);

    const conflictingFilterActive = isAnyFilterActive(
      sortConfig.conflictingFilters,
      validQueryParams,
      filterConfigs
    );

    const showCreateListingsLink = showCreateListingLinkForUser(config, currentUser);
    const sortBy = mode => {
      return sortConfig.active ? (
        <SortBy
          sort={validQueryParams[sortConfig.queryParamName]}
          isConflictingFilterActive={!!conflictingFilterActive}
          hasConflictingFilters={!!(sortConfig.conflictingFilters?.length > 0)}
          selectedFilters={selectedFilters}
          onSelect={this.handleSortBy}
          showAsPopup
          mode={mode}
          contentPlacementOffset={FILTER_DROPDOWN_OFFSET}
        />
      ) : null;
    };
    const noResultsInfo = (
      <NoSearchResultsMaybe
        listingsAreLoaded={listingsAreLoaded}
        totalItems={totalItems}
        location={location}
        resetAll={this.resetAll}
        showCreateListingsLink={showCreateListingsLink}
      />
    );

    const { title, description, schema } = createSearchResultSchema(
      listings,
      searchParamsInURL || {},
      intl,
      routeConfiguration,
      config
    );

    // Set topbar class based on if a modal is open in
    // a child component
    const topbarClasses = this.state.isMobileModalOpen
      ? classNames(css.topbarBehindModal, css.topbar)
      : css.topbar;

    // N.B. openMobileMap button is sticky.
    // For some reason, stickyness doesn't work on Safari, if the element is <button>

    const { isOpen } = this.state;
    return (
      <Page
        scrollingDisabled={scrollingDisabled}
        description={description}
        title={title}
        schema={schema}
      >
        <LayoutSingleColumn
          topbar={<TopbarContainer currentSearchParams={validQueryParams} />}
          footer={<FooterContainer />}
        >
          <div className={css.layoutWrapperContainer}>
            <SearchCategorySection viewport={viewport} />
            {isTrending && <TrendingSection listings={listings} />}
            <div className={css.middleFilters}>
              <MainPanelHeader
                className={css.mainPanel}
                sortByComponent={sortBy('desktop')}
                isSortByActive={sortConfig.active}
                listingsAreLoaded={listingsAreLoaded}
                resultsCount={totalItems}
                searchInProgress={searchInProgress}
                searchListingsError={searchListingsError}
                noResultsInfo={noResultsInfo}
              />
            </div>
            <div className={css.layoutWrapperMainWrapper}>
              <aside className={css.layoutWrapperFilterColumn} data-testid="filterColumnAside">
                <div className={css.filterColumnContent}>
                  {availableFilters.map(filterConfig => {
                    const key = `SearchFiltersDesktop.${filterConfig.scope || 'built-in'}.${
                      filterConfig.key
                    }`;
                    return (
                      <FilterComponent
                        key={key}
                        idPrefix="SearchFiltersDesktop"
                        className={css.filter}
                        config={filterConfig}
                        listingCategories={listingCategories}
                        marketplaceCurrency={marketplaceCurrency}
                        urlQueryParams={validQueryParams}
                        initialValues={initialValues(this.props, this.state.currentQueryParams)}
                        getHandleChangedValueFn={this.getHandleChangedValueFn}
                        intl={intl}
                        liveEdit
                        showAsPopup={false}
                        isDesktop
                      />
                    );
                  })}
                  <button className={css.resetAllButton} onClick={e => this.handleResetAll(e)}>
                    <FormattedMessage id={'SearchFiltersMobile.resetAll'} />
                  </button>
                </div>
              </aside>

              <div className={css.layoutWrapperMain} role="main">
                {noResultsInfo ? noResultsInfo : null}
                <div className={css.searchResultContainer}>
                  {isMobileLayout && (
                    <div className={css.searchFilterMobileSec}>
                      <div className={css.searchResultSummary}>
                        {searchInProgress ? (
                          <FormattedMessage id="MainPanelHeader.loadingResults" />
                        ) : (
                          <FormattedMessage
                            id="MainPanelHeader.foundResults"
                            values={{ count: totalItems }}
                          />
                        )}
                      </div>
                      <div className={css.searchFilterMobileHead}>
                        <button className={css.filterBtn} onClick={this.openMenu}>
                          Filter <Icons name="filter" />
                        </button>
                        {sortConfig.active ? (
                          <div className={css.sortyByWrapper}>{sortBy('mobile')}</div>
                        ) : null}
                      </div>
                      <Drawer
                        enableOverlay={true}
                        open={isOpen}
                        onClose={this.closeMenu}
                        direction="left"
                        className={css.topbarDrawer}
                      >
                        <div className={css.drawerBody}>
                          <div className={css.mobileFilterHead}>
                            <h2>Filter Products</h2>
                            <button className={css.closeModal} onClick={this.closeMenu}>
                              <Icons name="cross" />
                            </button>
                          </div>
                          <div className={css.mobileFilterContent}>{this.renderCurrentPanel()}</div>
                          <div className={css.mobileFilterFooter}>
                            <Button className={css.showListingsButton} onClick={this.closeMenu}>
                              <FormattedMessage
                                id="SearchFiltersMobile.showListings"
                                values={{
                                  count: totalItems,
                                }}
                              />
                            </Button>
                          </div>
                        </div>
                      </Drawer>
                    </div>
                  )}

                  <div
                    className={classNames(css.listingsForGridVariant, {
                      [css.newSearchInProgress]: !(listingsAreLoaded || searchListingsError),
                    })}
                  >
                    {searchListingsError ? (
                      <H3 className={css.error}>
                        <FormattedMessage id="SearchPage.searchError" />
                      </H3>
                    ) : null}
                    {!isValidDatesFilter ? (
                      <H5>
                        <FormattedMessage id="SearchPage.invalidDatesFilter" />
                      </H5>
                    ) : null}
                    <SearchResultsPanel
                      className={css.searchListingsPanel}
                      listings={listings}
                      pagination={listingsAreLoaded ? pagination : null}
                      search={parse(location.search)}
                      isMapVariant={false}
                      listingTypeParam={listingTypePathParam}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </LayoutSingleColumn>
      </Page>
    );
  }
}

/**
 * SearchPage component with grid layout (no map)
 *
 * @param {Object} props
 * @param {propTypes.currentUser} [props.currentUser] - The current user
 * @param {Array<propTypes.listing>} [props.listings] - The listings
 * @param {propTypes.pagination} [props.pagination] - The pagination
 * @param {boolean} [props.scrollingDisabled] - Whether the scrolling is disabled
 * @param {boolean} [props.searchInProgress] - Whether the search is in progress
 * @param {propTypes.error} [props.searchListingsError] - The search listings error
 * @param {Object} [props.searchParams] - The search params from the Redux state
 * @param {Function} [props.onManageDisableScrolling] - The function to manage the disable scrolling
 * @returns {JSX.Element}
 */
const EnhancedSearchPage = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();

  const searchListingsError = props.searchListingsError;
  if (isForbiddenError(searchListingsError)) {
    // This can happen if private marketplace mode is active
    return (
      <NamedRedirect
        name="SignupPage"
        state={{ from: `${location.pathname}${location.search}${location.hash}` }}
      />
    );
  }

  const { currentUser, ...restOfProps } = props;
  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const isUnauthorizedUser = currentUser && !isUserAuthorized(currentUser);
  const hasNoViewingRightsUser = currentUser && !hasPermissionToViewData(currentUser);
  const hasUserPendingApprovalError = isErrorUserPendingApproval(searchListingsError);
  const hasNoViewingRightsError = isErrorNoViewingPermission(searchListingsError);

  if ((isPrivateMarketplace && isUnauthorizedUser) || hasUserPendingApprovalError) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_USER_PENDING_APPROVAL }}
      />
    );
  } else if (hasNoViewingRightsUser || hasNoViewingRightsError) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_VIEW_LISTINGS }}
      />
    );
  }

  return (
    <SearchPageComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      history={history}
      location={location}
      currentUser={currentUser}
      {...restOfProps}
    />
  );
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  const {
    currentPageResultIds,
    pagination,
    searchInProgress,
    searchListingsError,
    searchParams,
  } = state.SearchPage;
  const listings = getListingsById(state, currentPageResultIds);

  return {
    currentUser,
    listings,
    pagination,
    scrollingDisabled: isScrollingDisabled(state),
    searchInProgress,
    searchListingsError,
    searchParams,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const SearchPage = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  ),
  withViewport
)(EnhancedSearchPage);

export default SearchPage;
